/**
 * Web stand-in for `react-native-date-picker`.
 *
 * Backed by the browser's native <input type="date|time|datetime-local">,
 * which Chrome and Edge on Windows render with a real picker and which works
 * with keyboard entry - the right behaviour for a desktop POS.
 *
 * Supports both shapes the native module offers, and that
 * zen-core-elements' <ZenDateTimePicker /> forwards:
 *   - modal:  <DatePicker modal open date onConfirm onCancel />
 *   - inline: <DatePicker date onDateChange />
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Modal, Pressable, StyleSheet, Text, View} from 'react-native';

export type DatePickerMode = 'date' | 'time' | 'datetime';

export type DatePickerProps = {
  date?: Date;
  mode?: DatePickerMode;
  modal?: boolean;
  open?: boolean;
  title?: string | null;
  confirmText?: string;
  cancelText?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  minuteInterval?: number;
  onConfirm?: (date: Date) => void;
  onCancel?: () => void;
  onDateChange?: (date: Date) => void;
  theme?: 'light' | 'dark' | 'auto';
  [key: string]: any;
};

const INPUT_TYPE: Record<DatePickerMode, string> = {
  date: 'date',
  time: 'time',
  datetime: 'datetime-local',
};

/** Formats a Date for an <input>, in local time (never UTC). */
function toInputValue(date: Date, mode: DatePickerMode): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const hm = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (mode === 'date') {
    return ymd;
  }
  if (mode === 'time') {
    return hm;
  }
  return `${ymd}T${hm}`;
}

/** Parses an <input> value back into a local Date, preserving the old date
 *  part when only a time was picked. */
function fromInputValue(value: string, mode: DatePickerMode, previous: Date): Date | null {
  if (!value) {
    return null;
  }
  if (mode === 'time') {
    const [h, m] = value.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) {
      return null;
    }
    const next = new Date(previous.getTime());
    next.setHours(h, m, 0, 0);
    return next;
  }
  const parsed = new Date(mode === 'date' ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function NativeDateInput({
  value,
  mode,
  minimumDate,
  maximumDate,
  onChange,
  autoFocus,
}: {
  value: Date;
  mode: DatePickerMode;
  minimumDate?: Date;
  maximumDate?: Date;
  onChange: (date: Date) => void;
  autoFocus?: boolean;
}) {
  return React.createElement('input', {
    type: INPUT_TYPE[mode],
    value: toInputValue(value, mode),
    min: minimumDate ? toInputValue(minimumDate, mode) : undefined,
    max: maximumDate ? toInputValue(maximumDate, mode) : undefined,
    autoFocus,
    onChange: (event: any) => {
      const next = fromInputValue(event.target.value, mode, value);
      if (next) {
        onChange(next);
      }
    },
    style: {
      fontSize: 16,
      padding: '10px 12px',
      borderRadius: 6,
      border: '1px solid #cccccc',
      fontFamily: 'inherit',
      width: '100%',
      boxSizing: 'border-box',
    },
  });
}

export function DatePicker(props: DatePickerProps) {
  const {
    date,
    mode = 'date',
    modal = false,
    open = false,
    title,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    minimumDate,
    maximumDate,
    onConfirm,
    onCancel,
    onDateChange,
  } = props;

  const initial = useMemo(() => date || new Date(), [date]);
  const [draft, setDraft] = useState<Date>(initial);

  // Keep the draft in step while the modal is closed, and reset it each time
  // the modal opens so Cancel really discards.
  useEffect(() => {
    setDraft(initial);
  }, [initial, open]);

  const handleInline = useCallback(
    (next: Date) => {
      setDraft(next);
      if (onDateChange) {
        onDateChange(next);
      }
    },
    [onDateChange],
  );

  if (!modal) {
    return (
      <View style={styles.inline}>
        <NativeDateInput
          value={draft}
          mode={mode}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          onChange={handleInline}
        />
      </View>
    );
  }

  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.backdrop} onPress={onCancel}>
        <Pressable style={styles.card} onPress={event => event.stopPropagation()}>
          {title ? <Text style={styles.title}>{title}</Text> : null}
          <NativeDateInput
            value={draft}
            mode={mode}
            minimumDate={minimumDate}
            maximumDate={maximumDate}
            onChange={setDraft}
            autoFocus
          />
          <View style={styles.actions}>
            <Pressable style={styles.button} onPress={onCancel}>
              <Text style={styles.cancelLabel}>{cancelText}</Text>
            </Pressable>
            <Pressable style={styles.button} onPress={() => onConfirm && onConfirm(draft)}>
              <Text style={styles.confirmLabel}>{confirmText}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  inline: {paddingVertical: 8},
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    padding: 20,
    minWidth: 320,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 12,
  },
  title: {fontSize: 16, fontWeight: '600', marginBottom: 12},
  actions: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: 18, gap: 8},
  button: {paddingVertical: 8, paddingHorizontal: 16, borderRadius: 6},
  cancelLabel: {color: '#666666', fontSize: 15},
  confirmLabel: {color: '#1976d2', fontSize: 15, fontWeight: '600'},
});

export default DatePicker;
